import React, { createContext, useContext, useCallback } from 'react';
import { useSystemConfig } from '../hooks/useSystemConfig';
import { resolveCurrencyAffixes, formatBalanceAmount } from '../utils/currency';

const SystemConfigContext = createContext(null);

export const useConfig = () => {
  const ctx = useContext(SystemConfigContext);
  if (!ctx) return { config: null, formatTime: (d) => '', formatDate: (d) => '', formatDateTime: (d) => '', formatWeight: (w) => '', formatCurrency: (v) => '', formatBalance: (v) => '' };
  return ctx;
};

export const SystemConfigProvider = ({ children }) => {
  const { data: config } = useSystemConfig();

  // ─── Shared timezone / format helpers ─────────────────────
  const tz       = config?.general?.timezone   || 'UTC';
  const is24h    = config?.general?.timeFormat === '24h';
  const tzOption = { timeZone: tz };

  // ─── Utility: Format time only (HH:MM) ─────────────────────
  const formatTime = useCallback((dateInput) => {
    if (!dateInput) return '';
    // Accept Date, ISO string, OR epoch-ms number (e.g. TanStack Query's
    // dataUpdatedAt). Anything else that's not already a Date → coerce once.
    const date = dateInput instanceof Date ? dateInput : new Date(dateInput);
    if (isNaN(date.getTime())) return '';
    if (is24h) {
      return date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false, ...tzOption });
    }
    return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true, ...tzOption });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tz, is24h]);

  // ─── Utility: Format date only (Mon DD, YYYY) ──────────────
  const formatDate = useCallback((dateInput) => {
    if (!dateInput) return '';
    // Accept Date, ISO string, OR epoch-ms number (e.g. TanStack Query's
    // dataUpdatedAt). Anything else that's not already a Date → coerce once.
    const date = dateInput instanceof Date ? dateInput : new Date(dateInput);
    if (isNaN(date.getTime())) return '';
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', ...tzOption });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tz]);

  // ─── Utility: Format full date + time ──────────────────────
  const formatDateTime = useCallback((dateInput) => {
    if (!dateInput) return '';
    // Accept Date, ISO string, OR epoch-ms number (e.g. TanStack Query's
    // dataUpdatedAt). Anything else that's not already a Date → coerce once.
    const date = dateInput instanceof Date ? dateInput : new Date(dateInput);
    if (isNaN(date.getTime())) return '';
    const datePart = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', ...tzOption });
    const timePart = is24h
      ? date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false, ...tzOption })
      : date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true, ...tzOption });
    return `${datePart} ${timePart}`;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tz, is24h]);

  // ─── Utility: Format weight based on config ────────────────
  const formatWeight = useCallback((lbs) => {
    if (lbs == null) return '';
    const useKg = config?.general?.weightUnit === 'kg';
    if (useKg) {
      const kg = Math.round(lbs * 0.453592);
      return `${kg} kg`;
    }
    return `${Math.round(lbs)} lbs`;
  }, [config?.general?.weightUnit]);

  // ─── Utility: Format currency based on config ──────────────
  // Tax is now folded into stored amounts server-side (booking.totalAmount and
  // every price the API returns is already tax-inclusive), so this formatter
  // must NOT re-apply tax — doing so would double-count. It is now a pure
  // currency formatter identical to formatBalance's numeric path.
  const formatCurrency = useCallback((amount) => {
    if (amount == null) return '';
    const { prefix, postfix } = resolveCurrencyAffixes(config);
    return `${prefix}${Number(amount).toFixed(2)}${postfix}`;
  }, [config?.finance]);

  // ─── Utility: Format a wallet balance amount ───────────────
  // Same currency symbol as formatCurrency, but NEVER applies tax —
  // a wallet balance is what's in the account, not a price being quoted.
  // Supports a signed display (e.g. "+$10.00" / "-$5.00") when signed=true.
  // BUG 428: one pure body (utils/currency.js) — cent-rounds before choosing
  // the sign, so a stored float artefact renders as clean money.
  const formatBalance = useCallback(
    (amount, opts) => formatBalanceAmount(amount, config, opts),
    [config?.finance],
  );

  // ─── Utility: Get credential refresh timeout ───────────────
  const credentialRefreshMs = (config?.reception?.credentialRefreshMinutes || 30) * 60 * 1000;

  const value = {
    config,
    formatTime,
    formatDate,
    formatDateTime,
    formatWeight,
    formatCurrency,
    formatBalance,
    credentialRefreshMs,
  };

  return (
    <SystemConfigContext.Provider value={value}>
      {children}
    </SystemConfigContext.Provider>
  );
};
